/**
 * DSL_Window
 * HUD-style window component
 */

import { memo, useCallback } from 'react'
import DSL_Socials from './DSL_Socials'

const HUD_COLOR = 'rgba(238, 58, 23, 0.6)'

const DSL_Window = memo(function DSL_Window({ onClose, isClosing, isTransitioning, scrollContentRef }) {
  // Enable flicker effects for all devices (memory leaks fixed)
  const shouldFlicker = isTransitioning
  
  // Prevent scroll propagation to canvas/body
  const handleContainerTouchMove = useCallback((e) => {
    // Let the window content handle its own scrolling
    e.stopPropagation()
  }, [])
  
  const handleContainerClick = useCallback((e) => {
    // Close if clicking the backdrop (outside the window)
    if (e.target.classList.contains('dsl-window-container')) {
      onClose()
    }
  }, [onClose])
  
  return (
    <div 
      className="dsl-window-container"
      onTouchMove={handleContainerTouchMove}
      onClick={handleContainerClick}
    >
      <div className={`dsl-window ${isClosing ? 'closing' : ''} ${shouldFlicker ? 'flickering' : ''}`}>
        <div className="dsl-window-background" />
        <DSL_WindowFrame />

        <div ref={scrollContentRef} className="dsl-window-content">
          {/* Horizontal logo everywhere except mobile portrait, which shows the
              stacked variant (toggled via CSS, same pattern as the Logo component). */}
          <img
            src="/images/dslBrand/DSL Logo Light and Orange.png"
            alt="Dark Star Labs"
            className="dsl-window-logo dsl-window-logo--horizontal"
          />
          <img
            src="/logos/DSL Logo Stacked Light and Orange.svg"
            alt="Dark Star Labs"
            className="dsl-window-logo dsl-window-logo--stacked"
          />
          <h1 className="dsl-window-title">
            Our mission is to <br></br><span style={{ color: '#ee3a17' }}>visualise the future </span>of space!
          </h1>
          <p className="dsl-window-text">
            Follow us on <span style={{ color: '#ee3a17' }}>social media</span> for more interactive space missions!
          </p>
          <DSL_Socials isTransitioning={isTransitioning} />
        </div>
      </div>
    </div>
  )
})

function DSL_WindowFrame() {
  // Use full-size dimensions - CSS will handle scaling per breakpoint
  const c1 = 80
  const c2 = 40
  const strokeWidth = 1
  const strokeWidthThin = 1
  
  return (
    <>
      {/* Top left corner */}
      <svg 
        className="dsl-window-corner dsl-window-corner-tl"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: c1,
          height: c1,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${c1} ${c1}`}
      >
        <line x1="0" y1={c1} x2="0" y2={c2} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        <line x1="0" y1={c2} x2={c2} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
        <line x1={c2} y1="0" x2={c1} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidth} />
      </svg>
      
      {/* Top right corner — closed chamfer (no close-button pocket), matching a
          DSL_SmallWindow rendered with hideCloseButton. */}
      <svg
        className="dsl-window-corner dsl-window-corner-tr"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: c1,
          height: c1,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${c1} ${c1}`}
      >
        <line x1={c1} y1={c1} x2={c1} y2={c2} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        <line x1={c1} y1={c2} x2={c1 - c2} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
        <line x1={c1 - c2} y1="0" x2="0" y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidth} />
      </svg>
      
      {/* Bottom left corner */}
      <svg 
        className="dsl-window-corner dsl-window-corner-bl"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: c1,
          height: c1,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${c1} ${c1}`}
      >
        <line x1="0" y1="0" x2="0" y2={c1 - c2} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        <line x1="0" y1={c1 - c2} x2={c2} y2={c1} stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
        <line x1={c2} y1={c1} x2={c1} y2={c1} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
      </svg>
      
      {/* Bottom right corner */}
      <svg 
        className="dsl-window-corner dsl-window-corner-br"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: c1,
          height: c1,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${c1} ${c1}`}
      >
        <line x1="0" y1={c1} x2={c1 - c2} y2={c1} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        <line x1={c1 - c2} y1={c1} x2={c1} y2={c1 - c2} stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
        <line x1={c1} y1={c1 - c2} x2={c1} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidth} />
      </svg>
    </>
  )
}

export default DSL_Window
